import type { AssistantMessage, CodeExecutionMeta, ConversationMessage, ToolCallDisplay, UserMessage } from "./types";
import type { ChannelMessage, PulseMessage } from "@/schemas/thread.schema";
import { startCase } from "lodash-es";
import { calculateSpeed, resolveSpeedDuration } from "./cost-utils";

// escalate_to_human is a function_call tool whose response comes from a human
// attendant, not the server — it can legitimately stay unanswered far longer
// than the 30s timeout below. `_escalate_to_human` is the retired name kept
// for historical threads created before the rename.
export const ESCALATE_TO_HUMAN_TOOL_NAMES = new Set(["escalate_to_human", "_escalate_to_human"]);

type AssistantPulseMessage = Extract<PulseMessage, { role: "assistant" }>;
type ToolPulseMessage = Extract<PulseMessage, { role: "tool" }>;
type UserPulseMessage = Extract<PulseMessage, { role: "user" }>;

export function findOpenEscalationToolCallId(history: PulseMessage[]): string | null {
  const answeredToolCalls = new Set(
    history
      .filter(message => message.role === "tool")
      .map(message => message.tool_call_id),
  );

  for (let index = history.length - 1; index >= 0; index--) {
    const message = history[index];
    if (message.role !== "assistant" || !message.tool_calls?.length) continue;

    const open = message.tool_calls.find(toolCall =>
      !!toolCall.id
      && toolCall.function.name === "escalate_to_human"
      && !answeredToolCalls.has(toolCall.id)
    );
    return open?.id ?? null;
  }

  return null;
}

export function sanitizeHistory(
  history: PulseMessage[],
  isStreaming: boolean = false,
  timeoutMs: number = 30000
): PulseMessage[] {
  const sanitized: PulseMessage[] = [];

  for (let i = 0; i < history.length; i++) {
    const message = history[i];
    sanitized.push(message);

    if (message.role !== "assistant" || !message.tool_calls?.length || isStreaming) continue;

    const toolResponsesFound = collectFollowingToolResponses(history, i + 1);
    for (const toolCall of message.tool_calls) {
      const timeoutResponse = buildTimedOutToolResponse(
        message,
        toolCall,
        toolResponsesFound,
        timeoutMs,
      );
      if (timeoutResponse) sanitized.push(timeoutResponse);
    }
  }

  return sanitized;
}

function collectFollowingToolResponses(history: PulseMessage[], start: number): Set<string> {
  // AI-1179: scan the whole rest of the history, not just the contiguous tool
  // block. The server now persists real (synthetic) error responses for orphan
  // calls; stopping at the first non-tool message would miss a persisted
  // answer sitting before the next user turn and inject a display-only
  // duplicate for the same tool_call_id.
  const found = new Set<string>();
  for (let index = start; index < history.length; index++) {
    const message = history[index];
    if (message.role !== "tool") continue;
    if (message.tool_call_id) found.add(message.tool_call_id);
  }
  return found;
}

function buildTimedOutToolResponse(
  message: AssistantPulseMessage,
  toolCall: NonNullable<AssistantPulseMessage["tool_calls"]>[number],
  responsesFound: Set<string>,
  timeoutMs: number,
): ToolPulseMessage | null {
  // Escalonamento aberto espera um humano, não um timeout de 30s: sem esta
  // exceção, cada escalonamento pendente vira bloco de erro no primeiro reload.
  if (ESCALATE_TO_HUMAN_TOOL_NAMES.has(toolCall.function.name)) return null;
  if (!toolCall.id || responsesFound.has(toolCall.id)) return null;

  const createdAt = message.metadata?.created_at;
  const messageTime = createdAt ? new Date(createdAt).getTime() : 0;
  if (messageTime && Date.now() - messageTime <= timeoutMs) return null;

  return {
    role: "tool",
    tool_call_id: toolCall.id,
    content: `Erro: A chamada da ferramenta "${toolCall.function.name}" falhou e não retornou uma resposta.`,
    metadata: { created_at: new Date().toISOString() },
  };
}

/**
 * Merge finalized voice turns (a live buffer that lives outside the activeThread-backed
 * history) after the canonical conversation, deduping any turn that canonical already
 * carries. Needed because fetchThread can swap in canonical data mid-session: if it lands
 * a turn that's also in the buffer, we'd otherwise render it twice. Match by (type, text);
 * turns without text (e.g. assistant tool-call messages) are always kept.
 */
export function mergeVoiceConversation(
  canonical: ConversationMessage[],
  voice: ConversationMessage[]
): ConversationMessage[] {
  const keyOf = (m: ConversationMessage) =>
    `${m.type}::${typeof m.content === "string" ? m.content.trim() : ""}`;
  const seen = new Set(canonical.map(keyOf));
  const voiceOnly = voice.filter((m) => {
    const text = typeof m.content === "string" ? m.content.trim() : "";
    return !text || !seen.has(keyOf(m));
  });
  return [...canonical, ...voiceOnly];
}

export function convertHistory(
  history: PulseMessage[],
  _model?: string,
  isStreaming: boolean = false,
  channelMessages: ChannelMessage[] = [],
): ConversationMessage[] {
  const sanitizedHistory = sanitizeHistory(history, isStreaming);
  const messages: ConversationMessage[] = [];
  const channelMessagesById = new Map(channelMessages.map(message => [message.id, message]));

  for (const message of sanitizedHistory) {
    if (message.role === "user") messages.push(toUserMessage(message));
    if (message.role === "assistant") {
      const assistant = toAssistantMessage(message, channelMessagesById);
      if (assistant) messages.push(assistant);
    }
    if (message.role === "tool") applyToolResponse(messages, message);
  }

  return messages;
}

function toUserMessage(message: UserPulseMessage): UserMessage {
  return {
    type: "user",
    content: message.content ?? "",
    name: startCase(message.name ?? ""),
    created_at: parseCreatedAt(message.metadata?.created_at),
    inputMethod: message.metadata?.input_method,
  };
}

function toAssistantMessage(
  message: AssistantPulseMessage,
  channelMessagesById: Map<string, ChannelMessage>,
): AssistantMessage | null {
  // A message that only carries reasoning is the "Thinking" bubble: the model has
  // not spoken yet, and dropping it would hide the whole thinking phase.
  if (!message.content && !message.tool_calls && !message.metadata?.reasoning) return null;

  const human = message.metadata?.human;
  const channelMessage = message.metadata?.id
    ? channelMessagesById.get(message.metadata.id)
    : undefined;
  return {
    type: "assistant",
    status: message.metadata?.id ? "completed" : "running",
    id: message.metadata?.id ?? "",
    content: message.content ?? "",
    isHumanAgent: !!human,
    humanAgentName: human?.operator_name,
    humanMessageId: channelMessage?.id ?? message.metadata?.id,
    deliveryStatus: channelMessage?.delivery_status ?? undefined,
    deliveryError: channelMessage?.last_error ?? undefined,
    reports: buildMessageReport(message, !!human),
    created_at: parseCreatedAt(message.metadata?.created_at),
    inputMethod: message.metadata?.input_method,
    // Persisted history carries no transient status, so the block renders closed
    // and finished ("done") rather than pretending the model is still thinking.
    reasoning: message.metadata?.reasoning,
    model: message.metadata?.model,
    tool_calls: message.tool_calls?.map(toolCall => ({
      name: toolCall.function.name,
      args: toolCall.function.arguments ?? "",
      tool_call_id: toolCall.id ?? "",
      status: "pending" as const,
    })),
  };
}

function buildMessageReport(
  message: AssistantPulseMessage,
  isHumanAgent: boolean,
): AssistantMessage["reports"] {
  if (isHumanAgent || !message.metadata?.usage) return undefined;
  const inputTokens = message.metadata.usage.input_tokens ?? 0;
  const outputTokens = message.metadata.usage.output_tokens ?? 0;
  // Medir a geracao do modelo quando o server a reporta; o fallback por
  // execution_time mantem o comportamento anterior para threads antigas.
  const speedDuration = resolveSpeedDuration(message.metadata);
  return {
    speed: calculateSpeed({ durationMs: speedDuration.durationMs, completionTokens: outputTokens }),
    speed_source: speedDuration.source,
    estimated_cost: message.metadata.cost ?? 0,
    execution_time: message.metadata.execution_time ?? 0,
    ...(message.metadata.generation_time !== undefined
      ? { generation_time: message.metadata.generation_time }
      : {}),
    ...(message.metadata.time_to_first_token !== undefined
      ? { time_to_first_token: message.metadata.time_to_first_token }
      : {}),
    completion_tokens: outputTokens,
    prompt_tokens: inputTokens,
    total_tokens: inputTokens + outputTokens,
    cached_tokens: message.metadata.usage.input_tokens_details?.cached_tokens,
  };
}

function applyToolResponse(messages: ConversationMessage[], message: ToolPulseMessage): void {
  const tool = findToolCall(messages, message.tool_call_id);
  if (!tool) return;

  tool.response = message.content ?? "";
  // AI-1195: response arrival time drives the duration display
  // (tool response metadata.created_at minus assistant message created_at).
  tool.responded_at = parseCreatedAt(message.metadata?.created_at);
  // AI-1179: a persisted error (status and/or metadata.error from the server)
  // marks the block; the legacy "Erro:" prefix check stays for display-only
  // gap fillers that carry no structured error.
  const persistedError = message.status === "error" || !!message.metadata?.error;
  tool.status = persistedError || tool.response.startsWith("Erro:") ? "error" : "completed";
  if (message.metadata?.error)
    tool.error = message.metadata.error;
  if (message.metadata?.escalation)
    tool.metadata = { ...tool.metadata, escalation: message.metadata.escalation };

  const meta = message.metadata as Record<string, unknown> | undefined;
  if (!meta?.code_execution) return;
  tool.metadata = {
    ...tool.metadata,
    tool_name: meta.tool_name as string,
    code_execution: meta.code_execution as CodeExecutionMeta,
  };
}

function findToolCall(
  messages: ConversationMessage[],
  toolCallId: string | undefined,
): ToolCallDisplay | undefined {
  for (let index = messages.length - 1; index >= 0; index--) {
    const message = messages[index];
    if (message.type !== "assistant") continue;
    const tool = message.tool_calls?.find(candidate => candidate.tool_call_id === toolCallId);
    if (tool) return tool;
  }
  return undefined;
}

function parseCreatedAt(value: string | undefined): Date | null {
  return value ? new Date(value) : null;
}
